import type * as React from "react";
import { useState } from "react";
import { AccessLevel, MediaType, type SchaefflerMediaDTO, TeaserType } from "../../dto";
import { type CMSTeaserProps } from "../../model";
import { ArrowLink, Link, LinkType } from "../../../common/components/Link";
import { IconSize } from "../../../common/components/icon/IconSize";
import { IconType } from "../../../common/components/icon/IconType";
import { Icon } from "../../../common/components/icon/Icon";
import { TeaserToggleIcon } from "~/common/components/icon/TeaserToggleIcon";
import classNames from "classnames";
import { _BLANK, BLANK, NARROW } from "../../../utils/constants";
import { MediasLogo, type MediasVariant } from "../../../Header/components/TopBar/MediasLogo";
import { useBooleanState } from "../../../common/hooks/useBooleanState";
import { Menu, useSimpleMenuState } from "../../../common/components/Menu/Menu";
import { MenuItem } from "../../../common/components/Menu/MenuItem";
import LoginPopup from "../../../Login/components/LoginPopup";
import ConsentPopup from "./ConsentPopup";
import ResetPasswordPopup from "../../../Login/components/ResetPasswordPopup";
import { isLoginTimeoutErrorVisible } from "../../../Login/loginUtils";
import ToastMessage from "../../../common/components/Messages/ToastMessage/ToastMessage";
import { Card, CardActionArea, IconButton, styled, useMediaQuery } from "@mui/material";
import { checkIsLocked, checkIsVisible, userGaveMarketingConsent } from "./CMSTeaserIsLocked";
import { consentIntro } from "./CMSTeaserConsent";
import { NEWSLETTER_ROLE, userIsLoggedIn } from "../../../utils/roles";
import { REGISTER_PAGE } from "../../../utils/url/staticUrls";
import { CMSMedia } from "../media/CMSMedia";
import { trackButtonClick, trackLogin, trackRegister } from "../../../feature/Analytics/ga/contentStream";
import { cleanupText, createMarkup } from "../CMSSection";
import { isMarketingConsentEnabled } from "../../../utils/features";
import { Carousel } from "../../../common/components/carousel/Carousel";
import { MEDIA_QUERY_MIN_TABLET } from "../../../utils/screenSizes";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/components/Button/Button";
import { getButtonSize, getButtonType } from "~/CMSComponents/CMSComponentHelpers";
import { resolveUrl } from "~/utils/url/urlResolver";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { C_ON_SURFACE_VARIANT } from "~/common/components/icon/Colors";
import { makeExternallyStyledElement } from "~/CMSComponents/CMSExternalStyleHelper";

const CMSTeaser = ({
  buttonSize = "DEFAULT",
  teaserType = TeaserType.STANDARD,
  headline,
  subHeadline,
  linkLabel,
  contentDropdown,
  toggles = false,
  buttonLabel,
  medias,
  imageClickable = false, // Off by default in favor of the main CTAs
  content,
  linkUrl,
  buttonType,
  index = 0,
  target = "",
  gated, // Used in Publication
  /**
   * "requires" marketing consent, not "gave" marketing consent
   */
  marketingConsent, // For Tests
  accessLevel = AccessLevel.general, // Used in Publication
  showMediaMetaData = false, // Used in Publication
  mediaMetaData, // Used in Publication
  onToggle,
  setContent,
  isExpanded = false,
  wideness = NARROW,
  className,
  mirrored = false,

  rootStyles,
  headlineStyles,
  subHeadlineStyles,
}: CMSTeaserProps) => {
  // These are "required" properties to make the teaser make sense.
  // Probably needs more valid combinations.
  const showContentBox: boolean = !!(headline || subHeadline || content || linkLabel || buttonLabel);

  const { t } = useTranslation();

  content = cleanupText(content);
  headline = cleanupText(headline);
  subHeadline = cleanupText(subHeadline);

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_TABLET);

  // Sanitizing the capitalized variables passed from JSP
  let accessLevelStr = accessLevel?.toLowerCase();

  const buttonProps = {
    ...getButtonSize(buttonSize),
    ...getButtonType(buttonType),
  };

  if (accessLevelStr === "plusbusiness") {
    accessLevelStr = "plus-business";
  }

  const useInvisibleLogic = teaserType === TeaserType.CTA;

  const isLocked = checkIsLocked({
    gated,
    marketingConsent,
    accessLevel,
  });

  let isVisible = true;

  if (useInvisibleLogic) {
    isVisible = checkIsVisible();
  }

  const {
    val: marketingConsentPopupOpen,
    setTrue: openMarketingConsent,
    setFalse: closeMarketingConsent,
  } = useBooleanState(false);

  const { val: loginPopupOpen, setTrue: openLogin, setFalse: closeLogin } = useBooleanState(false);
  const { val: resetPopupOpen, setTrue: openReset, setFalse: closeReset } = useBooleanState(false);

  // For now the consent popup title will use the headlines or the buttonLabel if provided.
  const popupTitle = headline || subHeadline || buttonLabel;

  const onCloseLogin = () => {
    closeLogin();
    trackLogin("Cancelled");
  };

  const onAcceptMarketingConsent = () => {
    window.SCHA.user.roles.push(NEWSLETTER_ROLE);
    closeMarketingConsent();
  };

  const [email, setEmail] = useState("");

  const onResetClick = (email: string) => {
    setEmail(email);
    closeLogin();
    openReset();
  };

  const onSignInClick = () => {
    openLogin();
  };

  const onRegisterClick = () => {
    trackRegister();
  };

  // Makes the whole teaser toggle the contentDropdown
  if (teaserType === TeaserType.DROPDOWN) {
    toggles = true;
  }

  if (teaserType === TeaserType.PUBLICATION) {
    buttonProps.color = "primary";
    buttonProps.variant = "contained";
    buttonProps.size = "medium";
  }

  const hasLink: boolean = !!linkUrl && Array.isArray(linkUrl) && !!linkUrl[0];

  let _linkUrl: string | undefined;

  if (linkUrl && linkUrl.length) {
    if (typeof linkUrl === "string") {
      _linkUrl = linkUrl;
    } else if (Array.isArray(linkUrl) && linkUrl[0].url) {
      _linkUrl = linkUrl[0].url;
    }
  }

  if (teaserType === TeaserType.CTA) {
    _linkUrl = `${REGISTER_PAGE}?ref=${window.location.href}`;
  }

  // Sets the default link to the first found url
  const [selectedLinkURL, setSelectedUrl] = useState<string | undefined>(_linkUrl);
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>();

  /**
   * Should pass on the event to the parent CMSTeasers component.
   * Passes on the HTML in `contentDropdown` to the CMSTeasers component.
   **/
  const toggle = (e: React.MouseEvent<unknown>) => {
    e.stopPropagation();
    // Only when this is a Dropdown Teaser with content
    if (!!contentDropdown && typeof index !== "undefined" && !!onToggle && !!setContent) {
      onToggle(index);
      setContent(contentDropdown);
    }
  };

  const isLoggedIn = userIsLoggedIn();

  const isUrlVisible = teaserType !== TeaserType.CTA || !gated || (gated && !isLocked);

  /**
   * Main Call To Action click
   * @returns void
   */
  const click = (e: React.MouseEvent<unknown>) => {
    e.stopPropagation();

    const validConfig = selectedLinkURL || (gated && isLocked);

    if (!validConfig) {
      if (import.meta.env.DEV) {
        console.warn("Please add a valid linkUrl");
      }
      return;
    }

    if (teaserType !== TeaserType.CTA) {
      if (gated && isLocked) {
        if (!isLoggedIn) {
          e.preventDefault();
          onSignInClick();
        } else if (marketingConsent && !userGaveMarketingConsent) {
          e.preventDefault();
          if (isMarketingConsentEnabled()) {
            openMarketingConsent();
          }
        }
      } else if (selectedLinkURL) {
        trackButtonClick(selectedLinkURL);
        window.location.assign(selectedLinkURL);
      }
      // CTA banner
    } else if (selectedLinkURL) {
      trackButtonClick(selectedLinkURL);
      window.location.assign(selectedLinkURL);
    }
  };

  const mediasLogoVariant: MediasVariant = accessLevelStr as MediasVariant;
  const mediasBlock: JSX.Element | undefined = <MediasLogo variant={mediasLogoVariant} />;
  let buttonIcon: IconType | undefined;
  let buttonDisabled: boolean = gated && isLocked && marketingConsent && userGaveMarketingConsent ? true : false;
  if (isLocked && isLoggedIn && !marketingConsent) {
    buttonDisabled = true;
  }

  const isTeaserDropdown: boolean = teaserType === TeaserType.DROPDOWN && !!contentDropdown;
  const showCTAContainer: boolean = !isTeaserDropdown && (!!linkLabel || !!buttonLabel);
  // Whole teaser block can be clicked on
  const teaserIsClickable: boolean = !imageClickable && !linkLabel && !buttonLabel && !!hasLink && !(gated && isLocked);
  // Only the image can be clicked on
  const teaserImageIsClickable: boolean = !!hasLink && imageClickable && !(gated && isLocked);
  const mediaDataPipe: JSX.Element = <span className="scha__pipe scha__pipe--text">|</span>;

  let lockBlock: JSX.Element | undefined;
  let authenticationBlock: JSX.Element | undefined;
  let mediumBlock: JSX.Element | undefined;
  let availableLanguagesMenu: JSX.Element | undefined;
  let ctaBlock: JSX.Element | undefined;
  let imageBlock: JSX.Element | undefined;
  let toggleButton: JSX.Element | undefined;
  let contentBlock: JSX.Element | undefined;

  // lockBlock
  if (gated && isLocked) {
    buttonIcon = IconType.Lock;

    lockBlock = (
      <div className="scha__teaser__lock">
        <span className="scha_teaser__lock__logo">{mediasBlock}</span>
        <span className="scha_teaser__lock__line scha__pipe scha__pipe--default">|</span>
        <span className="scha_teaser__lock__icon">
          <Icon size={IconSize.FONT_20} type={IconType.Lock} />
        </span>
      </div>
    );

    // When logged-in and marketing consent is necessary, we don't show the lock icon
    if (isLoggedIn && marketingConsent) {
      buttonIcon = undefined;
      lockBlock = undefined;
    }
  }

  // authenticationBlock
  if (gated && !isLoggedIn && teaserType !== TeaserType.STANDARD) {
    const registerParams = {
      href: `${REGISTER_PAGE}?ref=${window.location.href}`,
      target: "_blank",
    };
    authenticationBlock = (
      <div className="scha__teaser__authentication copy_left f-carbon">
        {teaserType !== TeaserType.CTA && (
          <Link onClick={onRegisterClick} className={"f-pre"} {...registerParams}>
            {t("header.link.register")}
          </Link>
        )}
        {teaserType === TeaserType.CTA && (
          <Button
            className={"button scha__teaser__button f-pre"}
            onClick={onRegisterClick}
            startIcon={<Icon type={IconType.User} />}
            {...registerParams}
          >
            {t("header.link.register")}
          </Button>
        )}
        <em className={"scha__teaser__authentication__or"}>{t("common.or")}</em>
        <Link onClick={onSignInClick} className={"f-pre"}>
          {t("header.link.signIn")}
        </Link>
      </div>
    );
  }

  // mediaMetaData
  if (showMediaMetaData) {
    if (linkUrl && Array.isArray(linkUrl)) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [isOpen, handleClick, handleClose, anchorEl] = useSimpleMenuState();

      const onLanguageMenuClick = (key: { language: string; url: string }) => {
        setSelectedUrl(key.url);
        setSelectedLanguage(key.language);
        handleClose();
      };

      if (linkUrl.length > 1) {
        availableLanguagesMenu = (
          <>
            <Link variant={LinkType.NAVIGATION} onClick={handleClick}>
              <span>
                {selectedLanguage && selectedLanguage.length
                  ? t(`language.${selectedLanguage.toLowerCase()}`)
                  : t("Language")}
              </span>
              <Icon
                className={"icon-margin-l"}
                type={isOpen ? IconType.ChevronUp : IconType.ChevronDown}
                size={IconSize.FONT_20}
              />
            </Link>
            <Menu open={isOpen} anchorEl={anchorEl} onClose={handleClose} MenuListProps={{ disablePadding: true }}>
              {linkUrl.map((key, index) => (
                <MenuItem
                  key={index}
                  onClick={() => {
                    return onLanguageMenuClick(key);
                  }}
                >
                  {t(`language.${key.language.toLowerCase()}`)}
                </MenuItem>
              ))}
            </Menu>
          </>
        );
      } else if (linkUrl.length === 1) {
        const key = linkUrl[0];
        availableLanguagesMenu = <span>{t(`language.${key.language.toLowerCase()}`)}</span>;
      }
    }

    mediumBlock = (
      <div className="scha__teaser__media-meta-data">
        {mediaMetaData?.medium && <span className="scha__teaser__media-meta-data__entry">{mediaMetaData?.medium}</span>}
        {mediaMetaData?.medium && mediaMetaData?.mimeType && mediaDataPipe}
        {mediaMetaData?.mimeType && (
          <span className="scha__teaser__media-meta-data__entry">{mediaMetaData?.mimeType}</span>
        )}
        {mediaMetaData?.medium && mediaMetaData?.length && mediaDataPipe}
        {mediaMetaData?.length && (
          <span className="scha__teaser__media-meta-data__entry">
            {mediaMetaData?.length} {mediaMetaData?.lengthUnit}
          </span>
        )}
        {mediaMetaData?.medium && mediaMetaData?.size && mediaDataPipe}
        {mediaMetaData?.size && (
          <span className="scha__teaser__media-meta-data__entry">
            {mediaMetaData?.size} {mediaMetaData?.sizeUnit}
          </span>
        )}
        {mediaMetaData?.medium && availableLanguagesMenu && mediaDataPipe}
        {availableLanguagesMenu && (
          <span className="scha__teaser__media-meta-data__entry">{availableLanguagesMenu}</span>
        )}
      </div>
    );
  }

  // imageBlock
  if (medias && medias.length > 0) {
    const renderMediaElement = (media: SchaefflerMediaDTO, hasMultipleVideos?: boolean, showCaption?: boolean) => {
      let element = (
        <figure
          className={classNames({
            scha__teaser__image: teaserType !== TeaserType.CONTACT,
            "scha__teaser__image--clickable": teaserImageIsClickable || isLocked,
            "scha__teaser__image--with-videos": hasMultipleVideos,
          })}
          onClick={(e) => (teaserImageIsClickable || isLocked ? click(e) : null)}
        >
          <CMSMedia
            {...media}
            ratio={teaserType === TeaserType.CONTACT ? "1/1" : "16/9"}
            disabled={isLocked}
            className={classNames({
              "scha__forced-ratio-image-circular": teaserType === TeaserType.CONTACT,
              "scha__forced-ratio-image-with-border-radius": teaserType === TeaserType.PUBLICATION,
            })}
          />

          {showCaption && media.caption && (
            <figcaption className={"scha__teaser__image__caption"}>{media.caption}</figcaption>
          )}
        </figure>
      );

      if (teaserImageIsClickable) {
        const linkParams = teaserIsClickable
          ? {
              href: isUrlVisible ? selectedLinkURL : undefined,
              target: isUrlVisible ? (isExternal(target) ? "_blank" : undefined) : undefined,
            }
          : {};

        element = <a {...linkParams}>{element}</a>;
      }

      return element;
    };
    let mediaElement;
    if (medias.length === 1) {
      mediaElement = renderMediaElement(medias[0], false, teaserType === TeaserType.PUBLICATION);
    } else {
      const containsVideos = medias.some((m) => m.type === MediaType.video);
      mediaElement = (
        <Carousel
          data={medias}
          pageItemsCount={1}
          renderSlide={(data) => renderMediaElement(data.data[0], containsVideos)}
          controlsVariant={"inside"}
        />
      );
    }

    imageBlock = (
      <div className="scha__teaser__image-container">
        {mediaElement}
        {lockBlock}
      </div>
    );
  }

  // imageBlock
  if (teaserType === TeaserType.CTA) {
    imageBlock = (
      <div className="scha__teaser__image-container">
        <div className="scha__teaser__image scha__teaser__image--medias">{mediasBlock}</div>
      </div>
    );
  }

  // contentBlock
  if (teaserType !== TeaserType.CTA) {
    contentBlock = (
      <div className={classNames(`scha__teaser__content scha__teaser__content--${teaserType}`, mirrored && "ml-0")}>
        {headline && (
          <StyledHeadline title={headline} rootStyles={headlineStyles} teaserType={teaserType}>
            {headline}
          </StyledHeadline>
        )}
        {subHeadline && (
          <StyledSubheadline title={subHeadline} rootStyles={subHeadlineStyles} teaserType={teaserType}>
            {subHeadline}
          </StyledSubheadline>
        )}
        {content && (
          <div className={"scha__teaser__text copy_left f-carbon"} dangerouslySetInnerHTML={createMarkup(content)} />
        )}
      </div>
    );
    // CTA
    // CTA uses `headline` for the _content_ area.
  } else {
    contentBlock = (
      <div className={classNames("scha__teaser__content scha__teaser__content--cta", mirrored && "ml-0")}>
        {headline && <div className={"scha__teaser__text copy_left f-carbon"}>{headline}</div>}
      </div>
    );
  }

  // Call To Action Container
  if (showCTAContainer) {
    const linkParams = {
      href: isUrlVisible ? resolveUrl(selectedLinkURL ?? "") : undefined,
      target: isUrlVisible ? (isExternal(target) ? "_blank" : undefined) : undefined,
    };
    ctaBlock = (
      <div
        className={classNames("scha__teaser__cta-container", {
          "scha__teaser__cta-container--CONTACT": teaserType === TeaserType.CONTACT,
          "scha__teaser__cta-container--PUBLICATION": teaserType === TeaserType.PUBLICATION,
          "ml-0": mirrored,
        })}
      >
        {/* CTA as a link */}
        {linkLabel && hasLink && (
          <ArrowLink className={"scha__teaser__link"} onClick={click} {...linkParams}>
            {linkLabel}
          </ArrowLink>
        )}
        {/* Main CTA button */}
        {(buttonLabel || buttonIcon) && (
          <Button
            className={`button scha__teaser__button scha__teaser__button--${teaserType}`}
            disabled={buttonDisabled}
            {...buttonProps}
            startIcon={buttonIcon ? <Icon type={buttonIcon} /> : undefined}
            onClick={click}
            {...linkParams}
          >
            {buttonLabel}
          </Button>
        )}
        {/* Authentication and Registration */}
        {authenticationBlock}
      </div>
    );
  }

  if (teaserType === TeaserType.CTA) {
    ctaBlock = (
      <div className={"scha__teaser__cta-container scha__teaser__cta-container--cta"}>{authenticationBlock}</div>
    );
  }

  if (isTeaserDropdown && contentDropdown) {
    toggleButton = (
      <div className="scha__teaser__togglebutton--container">
        <div className="scha__teaser__toggletext">
          {isExpanded ? t("newsletter.consent.seeless") : t("newsletter.consent.seemore")}
        </div>
        <IconButton className={"scha__teaser__cta scha__teaser__togglebutton"} onClick={toggle}>
          <TeaserToggleIcon
            isActive={isExpanded}
            size={IconSize.FONT_16}
            data-test-id={"cms-teaser-btn-toggle"}
            className="scha__teaser__togglebutton--icon"
          />
        </IconButton>
      </div>
    );
  }

  const isPublicationDesktop = teaserType === TeaserType.PUBLICATION && isDesktop;

  // If "mirrored" prop is checked, the text and the image swap the sides (it doesn't apply
  // for tablet/mobile viewport)
  const output = (
    <>
      {isPublicationDesktop ? !mirrored && imageBlock : imageBlock}
      {showContentBox && (
        <>
          <div
            className={classNames("scha__teaser__container", {
              "scha__teaser__container--CONTACT": teaserType === TeaserType.CONTACT,
              "scha__teaser__container--PUBLICATION": teaserType === TeaserType.PUBLICATION,
            })}
          >
            {contentBlock}
            {mediumBlock}
            {ctaBlock}
          </div>
          {toggleButton}
        </>
      )}
      {isPublicationDesktop && mirrored && imageBlock}
    </>
  );

  const teaserClick = (e: React.MouseEvent<unknown>) => {
    if (toggles) {
      return toggle(e);
    } else if (teaserIsClickable) {
      return click(e);
    }
  };

  const teaserLinkParams = teaserIsClickable
    ? {
        href: isUrlVisible ? selectedLinkURL : undefined,
        target: isUrlVisible ? (isExternal(target) ? "_blank" : undefined) : undefined,
      }
    : {};

  const TeaserTag = teaserIsClickable ? "a" : "div";

  const card = (
    <>
      <TeaserTag
        className={classNames(
          `scha__teaser scha__teaser--${teaserType} scha__teaser--${wideness}`,
          {
            "scha__teaser--hidden": !isVisible,
            "scha__teaser--active": isExpanded,
            "scha__teaser--clickable": teaserIsClickable,
            "scha__teaser--toggles": toggles,
            "scha__teaser--gated": gated,
            "scha__teaser--gated-locked": gated && isLocked,
            "scha__teaser--gated-unlocked": gated && !isLocked,
            "scha__teaser--gated-marketingconsent": gated && marketingConsent,
            "scha__teaser--loggedin": isLoggedIn,
            "scha__teaser--not-loggedin": !isLoggedIn,
          },
          className,
        )}
        id={`CMSTeaser-${index}`}
        onClick={teaserClick}
        {...teaserLinkParams}
      >
        {output}
      </TeaserTag>

      {loginPopupOpen && (
        <LoginPopup
          initialEmail={""}
          onResetClick={onResetClick}
          close={onCloseLogin}
          alternativeGAAction={"Sign In Content Stream"}
        />
      )}
      {resetPopupOpen && <ResetPasswordPopup initialEmail={email} close={closeReset} />}
      {marketingConsentPopupOpen && (
        <ConsentPopup
          close={closeMarketingConsent}
          consentIntro={consentIntro}
          popupTitle={popupTitle}
          onOk={onAcceptMarketingConsent}
        />
      )}

      {isLoginTimeoutErrorVisible() && <ToastMessage type={"info"}>{t("login.error.timeout")}</ToastMessage>}
    </>
  );

  if (!buttonLabel && !buttonIcon && teaserType === TeaserType.STANDARD && teaserIsClickable) {
    return (
      <Card
        sx={{
          display: "flex",
          borderWidth: "0px",
          height: "100%",
        }}
      >
        <CardActionArea
          sx={{
            display: "flex",
            alignItems: "stretch",
            borderRadius: "12px",
          }}
        >
          {card}
        </CardActionArea>
      </Card>
    );
  }

  return card;
};

const StyledCMSTeaser = styled(CMSTeaser, { shouldForwardProp: (prop) => prop !== "rootStyles" })(
  ({ rootStyles }) => rootStyles,
);

export default StyledCMSTeaser;

function isExternal(target: string | undefined) {
  target = target?.toUpperCase() ?? "";
  return target === BLANK || target === _BLANK;
}

interface HeadlineProps {
  title: string;
  rootStyles?: string;
  teaserType: TeaserType;
}

const BaseHeadline = styled("h3", {
  shouldForwardProp: (prop) => prop !== "teaserType",
})<HeadlineProps>(({ teaserType }) => ({
  ...getFontStyle(teaserType === TeaserType.CONTACT ? FontSize.HeadlineSmall : FontSize.TitleMedium),
  fontWeight: "bold",
}));

const BaseSubheadline = styled("h3", {
  shouldForwardProp: (prop) => prop !== "teaserType",
})<HeadlineProps>(({ teaserType }) => ({
  ...getFontStyle(teaserType === TeaserType.CONTACT ? FontSize.TitleMedium : FontSize.LabelLarge),
  fontWeight: "bold",
  color: C_ON_SURFACE_VARIANT,
}));

const StyledHeadline = makeExternallyStyledElement(BaseHeadline);
const StyledSubheadline = makeExternallyStyledElement(BaseSubheadline);
